"use client";
import { useState, useEffect } from "react";

interface FloatingCartProps {
  itemCount: number;
  children?: React.ReactNode;
  uid?: string;
  validationResult?: any;
  isOpen?: boolean;
  onClose?: () => void;
  onOpen?: () => void;
}

const FloatingCart = ({
  itemCount,
  children,
  uid,
  validationResult,
  isOpen: controlledIsOpen,
  onClose,
  onOpen,
}: FloatingCartProps) => {
  const [localIsOpen, setLocalIsOpen] = useState(false);
  const isControlled = controlledIsOpen !== undefined;
  const isOpen = isControlled ? controlledIsOpen : localIsOpen;

  const setIsOpen = (open: boolean) => {
    if (isControlled) {
      if (open) onOpen?.();
      else onClose?.();
    } else {
      setLocalIsOpen(open);
    }
  };

  useEffect(() => {
    const el = document.documentElement;
    if (isOpen) {
      const originalOverflow = el.style.overflow;
      el.style.overflow = "hidden";
      return () => {
        el.style.overflow = originalOverflow;
      };
    } else {
      el.style.overflow = "";
    }
  }, [isOpen]);

  const hasValidPlayer = !!(
    uid &&
    validationResult &&
    (validationResult.valid || validationResult.nickname)
  );

  const handleCartClick = () => {
    if (!hasValidPlayer) {
      const uidSection = document.getElementById("uid-verify");
      if (uidSection) {
        uidSection.scrollIntoView({ behavior: "smooth", block: "center" });

        const input = uidSection.querySelector("input");
        if (input) {
          setTimeout(() => {
            input.focus();
            input.classList.add("ring-2", "ring-amber-400", "ring-offset-2");
            setTimeout(() => {
              input.classList.remove(
                "ring-2",
                "ring-amber-400",
                "ring-offset-2",
              );
            }, 1800);
          }, 600);
        }
      }
      return;
    }

    setIsOpen(true);
  };

  return (
    <>
      <div className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-[100] print:hidden">
        {!isOpen && (
          <button
            onClick={handleCartClick}
            className="w-[58px] h-[58px] rounded-lg border shadow-lg flex items-center justify-center transition-transform hover:scale-105 active:scale-95"
            style={{ backgroundColor: "#FDFDFD", borderColor: "#5BA0E5" }}
            aria-label="Open cart"
          >
            <svg
              width="22"
              height="24"
              viewBox="0 0 22 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                x="0.5"
                y="6.5"
                width="21"
                height="17"
                rx="0.5"
                stroke="#5A98D8"
                strokeWidth="2"
              />
              <path
                d="M5.5 9V5.5C5.5 2.46243 7.96243 0 11 0C14.0376 0 16.5 2.46243 16.5 5.5V9"
                stroke="#5A98D8"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>

            {itemCount > 0 && (
              <span
                className="absolute -top-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                style={{ backgroundColor: "#5A98D8" }}
              >
                {itemCount}
              </span>
            )}
          </button>
        )}
      </div>

      {isOpen && (
        <div
          className="fixed inset-0 z-[101] bg-black/20 backdrop-blur-sm transition-opacity"
          onClick={() => setIsOpen(false)}
        />
      )}

      <div
        className={`fixed top-0 right-0 bottom-0 w-full md:w-[400px] z-[102] bg-white shadow-2xl transition-transform duration-300 ease-in-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
        style={{ borderLeft: "1px solid #DBEDFF" }}
        inert={!isOpen}
        aria-hidden={!isOpen}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between px-3 pt-6 pb-4">
            <button
              onClick={() => setIsOpen(false)}
              className="inline-flex items-center gap-3 hover:opacity-70 transition-opacity"
            >
              <svg
                width="6"
                height="10"
                viewBox="0 0 6 10"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5.207 0.5L0.707 5L5.207 9.5"
                  stroke="#111111"
                  strokeOpacity="0.84"
                  strokeLinecap="round"
                />
              </svg>
              <span className="font-mono text-[#111111] text-base font-semibold uppercase leading-5">
                Your cart
              </span>
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 hover:opacity-70 transition-opacity"
              aria-label="Close cart"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12.6914 1.5L1.5 12.6914"
                  stroke="#111111"
                  strokeOpacity="0.84"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
                <path
                  d="M12.6914 12.6914L1.5 1.5"
                  stroke="#111111"
                  strokeOpacity="0.84"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 flex flex-col">
            {isOpen &&
              (children ? (
                children
              ) : (
                <div className="text-center my-auto">
                  <svg
                    className="w-10 h-10 mx-auto mb-3 text-gray-200"
                    fill="none"
                    viewBox="0 0 22 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    <rect x="0.5" y="6.5" width="21" height="17" rx="0.5" />
                    <path
                      d="M5.5 9V5.5C5.5 2.46 7.96 0 11 0s5.5 2.46 5.5 5.5V9"
                      strokeLinecap="round"
                    />
                  </svg>
                  <p className="font-mono text-xs font-medium text-slate-600 uppercase">
                    Your cart is empty
                  </p>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="mt-6 uppercase text-xs tracking-widest px-6 py-2.5 rounded-sm transition-opacity hover:opacity-90"
                    style={{
                      backgroundColor: "#181818",
                      color: "white",
                    }}
                  >
                    Keep Browsing
                  </button>
                </div>
              ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default FloatingCart;
